import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import type { PlatformId, UiPreference } from '@prompt-optimize/core'
import { promptRepository } from '@prompt-optimize/storage'
import { useDebounceFn, usePreferredDark } from '@vueuse/core'
import { createLogger } from '@prompt-optimize/shared'

const log = createLogger('preference-store')

type Theme = 'light' | 'dark' | 'system'

export const usePreferenceStore = defineStore('preference', () => {
  const theme = ref<Theme>('system')
  const platform = ref<PlatformId>('midjourney')
  const hydrated = ref(false)
  const preferredDark = usePreferredDark()

  function applyTheme() {
    const resolved = theme.value === 'system' ? (preferredDark.value ? 'dark' : 'light') : theme.value
    const root = document.documentElement
    if (resolved === 'dark')
      root.classList.add('dark')
    else
      root.classList.remove('dark')
  }

  function setTheme(t: Theme) {
    theme.value = t
    applyTheme()
  }

  function toggleTheme() {
    const resolved = theme.value === 'system' ? (preferredDark.value ? 'dark' : 'light') : theme.value
    setTheme(resolved === 'dark' ? 'light' : 'dark')
  }

  function setPlatform(p: PlatformId) {
    platform.value = p
  }

  const lastDocumentId = ref<string | undefined>()

  function setLastDocumentId(id: string | undefined) {
    lastDocumentId.value = id
  }

  // ---- persistence ----
  const persist = useDebounceFn(async () => {
    if (!hydrated.value)
      return
    const pref: UiPreference = { theme: theme.value, platform: platform.value, lastDocumentId: lastDocumentId.value }
    await promptRepository.savePreference(pref).catch(e => log.error('persist preference', e))
  }, 300)

  watch([theme, platform, lastDocumentId], () => { void persist() })
  watch(preferredDark, () => { if (theme.value === 'system') applyTheme() })

  async function hydrate() {
    const saved = await promptRepository.getPreference().catch((e) => {
      log.error('hydrate preference', e)
      return undefined
    })
    if (saved) {
      theme.value = saved.theme
      platform.value = saved.platform
      lastDocumentId.value = saved.lastDocumentId
    }
    applyTheme()
    hydrated.value = true
  }

  return {
    theme,
    platform,
    lastDocumentId,
    hydrated,
    setTheme,
    toggleTheme,
    setPlatform,
    setLastDocumentId,
    hydrate,
  }
})
